// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import type { ExtractedDocument, TransactionEntry } from '../types/extraction';
import type {
  AutoFilledMap,
  TransactionHistoryGroup,
  WizardStepId,
} from '../types/wizard';

/**
 * Maps annual-statement fields to the wizard steps that own them. Each
 * extracted field key is rerouted to (stepId, fieldKey) so the wizard's
 * `setStepDataMap` patch lands in the right place and step completion
 * recomputes correctly.
 */
const ANNUAL_FIELD_TO_STEP: Record<string, { stepId: WizardStepId; fieldKey: string }> = {
  fullPolicyNumber:        { stepId: 'loa-validation',      fieldKey: 'fullPolicyNumber' },
  policyCategory:          { stepId: 'loa-validation',      fieldKey: 'policyCategory' },
  pensionType:             { stepId: 'loa-validation',      fieldKey: 'pensionType' },
  policyStatus:            { stepId: 'loa-validation',      fieldKey: 'policyStatus' },

  providerContactName:     { stepId: 'provider-details',    fieldKey: 'providerContactName' },
  completionDate:          { stepId: 'provider-details',    fieldKey: 'completionDate' },

  policyholderName:        { stepId: 'policyholder-details', fieldKey: 'policyholderName' },
  policyholderDateOfBirth: { stepId: 'policyholder-details', fieldKey: 'policyholderDateOfBirth' },
  policyholderCountryCode: { stepId: 'policyholder-details', fieldKey: 'policyholderCountryCode' },
  policyholderAddressLine1:{ stepId: 'policyholder-details', fieldKey: 'policyholderAddressLine1' },
  policyholderAddressLine2:{ stepId: 'policyholder-details', fieldKey: 'policyholderAddressLine2' },
  policyholderAddressLine3:{ stepId: 'policyholder-details', fieldKey: 'policyholderAddressLine3' },
  policyholderCity:        { stepId: 'policyholder-details', fieldKey: 'policyholderCity' },
  policyholderPostalCode:  { stepId: 'policyholder-details', fieldKey: 'policyholderPostalCode' },

  policyType:              { stepId: 'policy-details',      fieldKey: 'policyType' },
  policyStartDate:         { stepId: 'policy-details',      fieldKey: 'policyStartDate' },
  currentValue:            { stepId: 'policy-details',      fieldKey: 'currentValue' },
  currentValueDate:        { stepId: 'policy-details',      fieldKey: 'currentValueDate' },
  transferValue:           { stepId: 'policy-details',      fieldKey: 'transferValue' },
  transferValueDate:       { stepId: 'policy-details',      fieldKey: 'transferValueDate' },

  payingStatus:                 { stepId: 'contribution-details', fieldKey: 'payingStatus' },
  transfersInFromAnotherScheme: { stepId: 'contribution-details', fieldKey: 'transfersInFromAnotherScheme' },
  employerContribution:         { stepId: 'contribution-details', fieldKey: 'employerContribution' },
  employeeContribution:         { stepId: 'contribution-details', fieldKey: 'employeeContribution' },

  hasReceivedTransfers:    { stepId: 'transfers-in',        fieldKey: 'hasReceivedTransfers' },

  exitChargesDetails:      { stepId: 'charges-fees',        fieldKey: 'exitChargesDetails' },
  transferOutCosts:        { stepId: 'charges-fees',        fieldKey: 'transferOutCosts' },
  guaranteedReturnsDetails:{ stepId: 'charges-fees',        fieldKey: 'guaranteedReturnsDetails' },
  terminalBonusDetails:    { stepId: 'charges-fees',        fieldKey: 'terminalBonusDetails' },
  otherChargeDetails:      { stepId: 'charges-fees',        fieldKey: 'otherChargeDetails' },
};

export interface AppliedExtractionPatch {
  /** Field updates per step, ready to merge into stepDataMap */
  stepUpdates: Partial<Record<WizardStepId, Record<string, unknown>>>;
  /** Per-step → per-field provenance map */
  autoFilled: AutoFilledMap;
  /** Transaction history group (if a transactions PDF was parsed) */
  transactionHistory?: TransactionHistoryGroup;
  /** Total fields populated across all steps */
  fieldsPopulated: number;
  /** Total transactions parsed */
  transactionsParsed: number;
  /** Source filenames keyed by document kind, for "auto-filled from <file>" badges */
  sources: Record<string, string>;
  warnings: string[];
}

/** Pure function — given extracted documents, build a wizard patch. */
export function applyExtraction(
  documents: ExtractedDocument[],
): AppliedExtractionPatch {
  const stepUpdates: Partial<Record<WizardStepId, Record<string, unknown>>> = {};
  const autoFilled: AutoFilledMap = {};
  let transactionHistory: TransactionHistoryGroup | undefined;
  let fieldsPopulated = 0;
  let transactionsParsed = 0;
  const sources: Record<string, string> = {};
  const warnings: string[] = [];

  for (const doc of documents) {
    if (doc.kind === 'UNKNOWN') {
      warnings.push(`Unrecognised PDF format: ${doc.sourceFileName} — ${doc.reason}`);
      continue;
    }

    if (doc.kind === 'STANDARD_LIFE_ANNUAL') {
      sources.annual = doc.sourceFileName;
      for (const [fieldKey, value] of Object.entries(doc.fields)) {
        if (value === undefined || value === null || value === '') continue;

        // Investment funds get routed to the investments step as a
        // structured array — they're handled separately from the flat
        // field map.
        if (fieldKey === 'investmentFunds') {
          const rows = value as Array<Record<string, unknown>>;
          if (!Array.isArray(rows) || rows.length === 0) continue;
          const bucket = (stepUpdates['investments'] ??= {});
          bucket.investmentFunds = rows.map((r) => ({
            fundName: (r.fundName as string) ?? '',
            allocationPercent:
              r.allocationPercent !== undefined ? String(r.allocationPercent) : undefined,
            futureContributionPercent:
              r.allocationPercent !== undefined ? String(r.allocationPercent) : undefined,
            annualManagementCharge:
              r.annualManagementCharge !== undefined
                ? String(r.annualManagementCharge)
                : undefined,
          }));
          const provenance = (autoFilled['investments'] ??= {});
          provenance.investmentFunds = 'extracted';
          fieldsPopulated++;
          continue;
        }

        const target = ANNUAL_FIELD_TO_STEP[fieldKey];
        if (!target) continue;
        const bucket = (stepUpdates[target.stepId] ??= {});
        bucket[target.fieldKey] = value;
        const provenance = (autoFilled[target.stepId] ??= {});
        provenance[target.fieldKey] = 'extracted';
        fieldsPopulated++;
      }
      continue;
    }

    if (doc.kind === 'STANDARD_LIFE_TRANSACTIONS') {
      sources.transactions = doc.sourceFileName;
      const tx = doc.transactions;
      transactionHistory = {
        periodFrom: tx.periodFrom,
        periodTo: tx.periodTo,
        openingValue: tx.openingValue,
        closingValue: tx.closingValue,
        totalPaymentsIn: tx.totalPaymentsIn,
        totalPaymentsOut: tx.totalPaymentsOut,
        entries: tx.entries.map((e) => ({
          processedDate: e.processedDate,
          type: e.type,
          description: e.description,
          amountIn: e.amountIn,
          amountOut: e.amountOut,
          allocationRatePercent: e.allocationRatePercent,
          contributionPaid: e.contributionPaid,
          legs: e.legs.map((l) => ({
            action: l.action,
            units: l.units,
            fundName: l.fundName,
            unitPriceCents: l.unitPriceCents,
            amount: l.amount,
          })),
          rawText: e.rawText,
        })),
        sourceDocumentRef: doc.sourceFileName,
      };
      transactionsParsed = tx.entries.length;

      // Cross-document enrichment: derive Step 7 fields from the entries
      const derived = deriveContributionFields(tx.entries);
      if (Object.keys(derived).length > 0) {
        const bucket = (stepUpdates['contribution-details'] ??= {});
        const provenance = (autoFilled['contribution-details'] ??= {});
        for (const [k, v] of Object.entries(derived)) {
          if (v === undefined || v === '') continue;
          if (bucket[k] === undefined) {
            bucket[k] = v;
            provenance[k] = 'extracted';
            fieldsPopulated++;
          }
        }
      }
    }
  }

  return {
    stepUpdates,
    autoFilled,
    transactionHistory,
    fieldsPopulated,
    transactionsParsed,
    sources,
    warnings,
  };
}

/**
 * Derive Step 7 (Contribution Details) fields from the parsed transaction
 * entries (per `standardlife-statement-extraction.md` §2.7).
 */
function deriveContributionFields(entries: TransactionEntry[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (entries.length === 0) return out;

  const regulars = entries.filter((e) => e.type === 'REGULAR_CONTRIBUTION');
  const singles = entries.filter((e) => e.type === 'SINGLE_CONTRIBUTION');

  // Modal regular premium
  if (regulars.length > 0) {
    const counts = new Map<number, number>();
    for (const r of regulars) {
      const amt = r.contributionPaid ?? r.amountIn ?? 0;
      if (!amt) continue;
      counts.set(amt, (counts.get(amt) ?? 0) + 1);
    }
    let mode = 0;
    let modeCount = 0;
    for (const [amt, c] of counts) {
      if (c > modeCount) {
        modeCount = c;
        mode = amt;
      }
    }
    if (mode > 0) out.regularPremium = String(mode);

    // Cadence: if successive entries are ~30 days apart → Monthly
    if (regulars.length >= 2) {
      const sorted = [...regulars].sort((a, b) =>
        a.processedDate.localeCompare(b.processedDate),
      );
      const gaps: number[] = [];
      for (let i = 1; i < sorted.length; i++) {
        const a = new Date(sorted[i - 1].processedDate).getTime();
        const b = new Date(sorted[i].processedDate).getTime();
        gaps.push((b - a) / (1000 * 60 * 60 * 24));
      }
      const avg = gaps.reduce((s, g) => s + g, 0) / gaps.length;
      if (avg >= 25 && avg <= 35) out.regularPremiumFrequency = 'Monthly';
      else if (avg >= 85 && avg <= 95) out.regularPremiumFrequency = 'Quarterly';
      else if (avg >= 360 && avg <= 370) out.regularPremiumFrequency = 'Annually';
    }
  }

  if (singles.length > 0) {
    const total = singles.reduce((s, e) => s + (e.amountIn ?? 0), 0);
    if (total > 0) out.singlePremium = String(total);
    out.singlePremiumFrequency = 'SINGLE';
  }

  // Date of last contribution = max processedDate across contribution entries
  const contributionDates = [...regulars, ...singles]
    .map((e) => e.processedDate)
    .filter(Boolean)
    .sort();
  if (contributionDates.length > 0) {
    out.dateOfLastContribution = contributionDates[contributionDates.length - 1];
  }

  // Allocation rate: if every entry has 100% allocation, set to 100
  const allHave100 =
    [...regulars, ...singles].length > 0 &&
    [...regulars, ...singles].every(
      (e) => e.allocationRatePercent != null && e.allocationRatePercent >= 99.99,
    );
  if (allHave100) out.allocationPercentContributions = '100';

  return out;
}
